'use client';

import { useIsLargeTablet } from '@workspace/ui/hooks';
import type { MenuItem, NotificationCategory } from '@workspace/ui/types';
import {
  Banknote,
  BriefcaseBusiness,
  CalendarDays,
  CalendarPlus,
  ChartColumn,
  CircleHelp,
  CreditCard,
  FileClock,
  FileSpreadsheet,
  FileText,
  LayoutGrid,
  ReceiptText,
  Settings,
  User,
  Users,
  WalletCards,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { NOTIFICATION_CATEGORIES } from '../../lib/constants';
import { ThemeToggle } from '../custom/menu/theme-toggle';
import { AppNavbar } from './app-navbar';
import { AppSidebar } from './app-sidebar';

export const APP_WALLET = 'wallet' as const;
export const APP_EOR = 'eor' as const;
export type AppNav = typeof APP_WALLET | typeof APP_EOR;

/**
 * Menu titles use sidebar.* and global.* translation keys. Use the same user-facing strings in EOR
 * and Wallet locale files for each key; labels are not switched by host app in this component.
 */
const KNOWLEDGE_BASE_URL = 'https://help.nativeteams.com/' as const;

function getNotificationCount(
  category: NotificationCategory | undefined,
  notificationCountByCategory?: Partial<Record<NotificationCategory, number>>
): number {
  if (!category || !notificationCountByCategory) return 0;
  return notificationCountByCategory[category] ?? 0;
}

function getPayrollNotificationCategory(
  featureFlags: Record<string, boolean>
): NotificationCategory | undefined {
  return featureFlags?.['show-payroll-nav-badges'] ? NOTIFICATION_CATEGORIES.PAYROLL : undefined;
}

function resolvePayrollToRunNavUrl(params: {
  isGlobalAdmin?: boolean;
  isEmployer?: boolean;
  featureFlags: Record<string, boolean>;
  getLinkUrl: (path: string, targetApp: AppNav) => string;
}): string | undefined {
  const { isGlobalAdmin, isEmployer, featureFlags, getLinkUrl } = params;

  if (!featureFlags?.['show-payroll-to-run']) return undefined;

  if (isGlobalAdmin) return getLinkUrl('/payroll-to-run', APP_EOR);
  if (!isEmployer) return undefined;
  return getLinkUrl('/payroll-to-run', APP_EOR);
}

function applyNotificationBadgesToItems(
  items: MenuItem[],
  notificationCountByCategory?: Partial<Record<NotificationCategory, number>>
): MenuItem[] {
  return items.map((item) => {
    if (item.items?.length) {
      const subitemsWithBadge = item.items.map((sub) => {
        const count = getNotificationCount(sub.category, notificationCountByCategory);
        return { ...sub, ...(count > 0 ? { badge: count } : {}) };
      });
      const parentSum = subitemsWithBadge.reduce((sum, sub) => sum + (sub.badge ?? 0), 0);
      return {
        ...item,
        items: subitemsWithBadge,
        ...(parentSum > 0 ? { badge: parentSum } : {}),
      };
    }
    const count = getNotificationCount(item.category, notificationCountByCategory);
    return { ...item, ...(count > 0 ? { badge: count } : {}) };
  });
}

function payrollNavGroup(params: {
  app: AppNav;
  t: (key: string) => string;
  monthlyInputsUrl: string;
  payrollUrl: string;
  /** When set (employer / contractor), inserts Payroll to run after EOR payroll. */
  payrollToRunUrl?: string;
  monthlyInputsCategory?: NotificationCategory;
  /** When false, omit EOR payroll (e.g. contractor team lead). Default true. */
  includeEorPayroll?: boolean;
}): MenuItem {
  const {
    app,
    t,
    monthlyInputsUrl,
    payrollUrl,
    payrollToRunUrl,
    monthlyInputsCategory,
    includeEorPayroll = true,
  } = params;

  const subitems = [
    {
      title: app === APP_WALLET ? 'Monthly inputs' : t('sidebar.monthlyInputs'),
      url: monthlyInputsUrl,
      ...(monthlyInputsCategory ? { category: monthlyInputsCategory } : {}),
    },
    ...(includeEorPayroll
      ? [
          {
            title: app === APP_WALLET ? 'EOR payroll' : t('sidebar.eorPayroll'),
            url: payrollUrl,
          },
        ]
      : []),
    ...(payrollToRunUrl
      ? [
          {
            title: t('sidebar.payrollToRun'),
            url: payrollToRunUrl,
          },
        ]
      : []),
  ];

  if (subitems.length === 1) {
    const onlySubitem = subitems[0]!;
    return {
      title: onlySubitem.title,
      url: onlySubitem.url,
      icon: <Banknote />,
      urlPrefix: '/monthly-inputs',
      ...(onlySubitem.category ? { category: onlySubitem.category } : {}),
    };
  }

  return {
    title: t('global.payroll'),
    url: monthlyInputsUrl,
    icon: <Banknote />,
    urlPrefix: '/monthly-inputs',
    items: subitems,
  };
}

function billingNavItem(
  t: (key: string) => string,
  billingUrl: string,
  category?: NotificationCategory
): MenuItem {
  return {
    title: t('sidebar.billing'),
    url: billingUrl,
    icon: <FileSpreadsheet />,
    ...(category ? { category } : {}),
  };
}

function paymentsNavGroup(params: {
  t: (key: string) => string;
  billingUrl: string;
  contractorPaymentsUrl?: string;
}): MenuItem {
  const { t, billingUrl, contractorPaymentsUrl } = params;

  return {
    title: t('sidebar.paymentsGroup'),
    url: billingUrl,
    icon: <CreditCard />,
    items: [
      {
        title: t('sidebar.billing'),
        url: billingUrl,
        category: NOTIFICATION_CATEGORIES.INVOICES,
      },
      ...(contractorPaymentsUrl
        ? [
            {
              title: t('sidebar.contractors'),
              url: contractorPaymentsUrl,
            },
          ]
        : []),
    ],
  };
}

function peopleNavItem(params: {
  app: AppNav;
  t: (key: string) => string;
  getLinkUrl: (path: string, targetApp: AppNav) => string;
}): MenuItem {
  const peopleUrl = params.getLinkUrl('/people', APP_EOR);
  return {
    title: params.t('sidebar.people'),
    url: peopleUrl,
    ...(params.app === APP_EOR ? { urlPrefix: '/people' } : {}),
    icon: <Users />,
  };
}

function payslipsNavItem(params: {
  app: AppNav;
  t: (key: string) => string;
  getLinkUrl: (path: string, targetApp: AppNav) => string;
}): MenuItem {
  const payslipsUrl = params.getLinkUrl('/payslips', APP_EOR);
  return {
    title: params.t('sidebar.payslips'),
    url: payslipsUrl,
    ...(params.app === APP_EOR ? { urlPrefix: '/payslips' } : {}),
    icon: <FileText />,
  };
}

function reportsNavItems(params: {
  app: AppNav;
  t: (key: string) => string;
  getLinkUrl: (path: string, targetApp: AppNav) => string;
  enabled: boolean;
}): MenuItem[] {
  if (!params.enabled) return [];
  return [
    {
      title: params.app === APP_WALLET ? 'Reports' : params.t('sidebar.reports'),
      url: params.getLinkUrl('/reports', APP_EOR),
      icon: <ChartColumn />,
    },
  ];
}

function absencesNavItems(params: {
  app: AppNav;
  t: (key: string) => string;
  getLinkUrl: (path: string, targetApp: AppNav) => string;
  enabled: boolean;
}): MenuItem[] {
  if (!params.enabled) return [];
  const absencesUrl = params.getLinkUrl('/absences', APP_EOR);
  return [
    {
      title: params.t('sidebar.absences'),
      url: absencesUrl,
      ...(params.app === APP_EOR ? { urlPrefix: '/absences' } : {}),
      icon: <CalendarDays />,
    },
  ];
}

/** Payroll / monthly-inputs rows and optional billing (split so main nav can match global order: … Payroll, Reports, Wallet, Billing …). */
function getEmployedEmployeePayrollAndBilling(params: {
  isGlobalAdmin: boolean;
  isTeamLead: boolean;
  app: AppNav;
  t: (key: string) => string;
  getLinkUrl: (path: string, targetApp: AppNav) => string;
  monthlyInputsUrl: string;
  payrollUrl: string;
  billingUrl: string;
  featureFlags: Record<string, boolean>;
}): { payrollItems: MenuItem[]; billingItem: MenuItem | null } {
  const {
    featureFlags,
    isGlobalAdmin,
    isTeamLead,
    app,
    t,
    getLinkUrl,
    monthlyInputsUrl,
    payrollUrl,
    billingUrl,
  } = params;

  const payrollToRunUrl = resolvePayrollToRunNavUrl({ isGlobalAdmin, featureFlags, getLinkUrl });
  const payrollNotificationCategory = getPayrollNotificationCategory(featureFlags);

  if (isGlobalAdmin) {
    return {
      payrollItems: [
        payrollNavGroup({
          app,
          t,
          monthlyInputsUrl,
          payrollUrl,
          payrollToRunUrl,
          monthlyInputsCategory: payrollNotificationCategory,
        }),
      ],
      billingItem: billingNavItem(t, billingUrl),
    };
  }

  if (isTeamLead) {
    return {
      payrollItems: [
        payrollNavGroup({
          app,
          t,
          monthlyInputsUrl,
          payrollUrl,
          monthlyInputsCategory: payrollNotificationCategory,
          includeEorPayroll: false,
        }),
      ],
      billingItem: null,
    };
  }

  // Non–global-admin employees: monthly inputs only (not /payroll), on every breakpoint.
  return {
    payrollItems: [
      {
        title: t('global.monthlyInputs'),
        url: monthlyInputsUrl,
        icon: <CalendarPlus />,
        urlPrefix: '/monthly-inputs',
        ...(payrollNotificationCategory ? { category: payrollNotificationCategory } : {}),
      },
    ],
    billingItem: null,
  };
}

function buildEmployerMainItems(params: {
  app: AppNav;
  t: (key: string) => string;
  getLinkUrl: (path: string, targetApp: AppNav) => string;
  featureFlags: Record<string, boolean>;
  walletLink: MenuItem;
  expensesLink: MenuItem;
  contractorPaymentsLink: MenuItem;
  clientInvoicingLink: MenuItem;
}): MenuItem[] {
  const {
    app,
    t,
    getLinkUrl,
    featureFlags,
    walletLink,
    expensesLink,
    contractorPaymentsLink,
    clientInvoicingLink,
  } = params;
  const dashboardUrl = getLinkUrl('/', APP_EOR);
  const monthlyInputsUrl = getLinkUrl('/monthly-inputs', APP_EOR);
  const payrollToRunUrl = resolvePayrollToRunNavUrl({ isEmployer: true, featureFlags, getLinkUrl });
  const payrollUrl = getLinkUrl('/payroll', APP_EOR);
  const billingUrl = getLinkUrl('/billing', APP_EOR);
  const contractorPaymentsUrl = featureFlags?.['show-contractor-payments']
    ? contractorPaymentsLink.url
    : undefined;

  return [
    {
      title: t('sidebar.dashboard'),
      url: dashboardUrl,
      icon: <LayoutGrid />,
    },
    peopleNavItem({ app, t, getLinkUrl }),
    ...absencesNavItems({
      app,
      t,
      getLinkUrl,
      enabled: true,
    }),
    expensesLink,
    payrollNavGroup({
      app,
      t,
      monthlyInputsUrl,
      payrollUrl,
      payrollToRunUrl,
      monthlyInputsCategory: getPayrollNotificationCategory(featureFlags),
    }),
    ...reportsNavItems({
      app,
      t,
      getLinkUrl,
      enabled: true,
    }),
    walletLink,
    clientInvoicingLink,
    paymentsNavGroup({
      t,
      billingUrl,
      contractorPaymentsUrl,
    }),
  ];
}

function buildContractorMainItems(params: {
  app: AppNav;
  t: (key: string) => string;
  getLinkUrl: (path: string, targetApp: AppNav) => string;
  featureFlags: Record<string, boolean>;
  walletLink: MenuItem;
  clientInvoicingLink: MenuItem;
  expensesLink: MenuItem;
  contractorPaymentsLink: MenuItem;
  isGlobalAdmin: boolean;
  isTeamLead: boolean;
}): MenuItem[] {
  const {
    app,
    t,
    getLinkUrl,
    featureFlags,
    walletLink,
    clientInvoicingLink,
    expensesLink,
    contractorPaymentsLink,
    isGlobalAdmin,
    isTeamLead,
  } = params;

  const profileUrl = getLinkUrl('/profile', APP_EOR);
  const monthlyInputsUrl = getLinkUrl('/monthly-inputs', APP_EOR);

  const payrollUrl = getLinkUrl('/payroll', APP_EOR);
  const billingUrl = getLinkUrl('/billing', APP_EOR);
  const profileItem: MenuItem = { title: t('sidebar.profile'), url: profileUrl, icon: <User /> };

  const teamLeadPayrollItems: MenuItem[] =
    isTeamLead && !isGlobalAdmin
      ? [
          payrollNavGroup({
            app,
            t,
            monthlyInputsUrl,
            payrollUrl,
            includeEorPayroll: false,
          }),
        ]
      : [];

  const contractorPaymentsIfEnabled = featureFlags?.['show-contractor-payments']
    ? [contractorPaymentsLink]
    : [];

  const payrollToRunUrl = resolvePayrollToRunNavUrl({ isGlobalAdmin, featureFlags, getLinkUrl });

  if (isGlobalAdmin) {
    return [
      peopleNavItem({ app, t, getLinkUrl }),
      ...absencesNavItems({
        app,
        t,
        getLinkUrl,
        enabled: true,
      }),
      expensesLink,
      payrollNavGroup({
        app,
        t,
        monthlyInputsUrl,
        payrollUrl,
        payrollToRunUrl,
      }),
      ...reportsNavItems({
        app,
        t,
        getLinkUrl,
        enabled: true,
      }),
      walletLink,
      billingNavItem(t, billingUrl),
      clientInvoicingLink,
      ...contractorPaymentsIfEnabled,
      profileItem,
    ];
  }

  return [
    walletLink,
    profileItem,
    expensesLink,
    clientInvoicingLink,
    ...teamLeadPayrollItems,
    ...contractorPaymentsIfEnabled,
  ];
}

function buildGigWorkerMainItems(params: {
  t: (key: string) => string;
  getLinkUrl: (path: string, targetApp: AppNav) => string;
  featureFlags: Record<string, boolean>;
  walletLink: MenuItem;
  clientInvoicingLink: MenuItem;
}): MenuItem[] {
  const { t, getLinkUrl, walletLink, clientInvoicingLink } = params;

  const profileUrl = getLinkUrl('/profile', APP_EOR);

  return [
    walletLink,
    {
      title: t('sidebar.profile'),
      url: profileUrl,
      icon: <User />,
    },
    clientInvoicingLink,
  ];
}

function buildB2CStandardMainItems(params: {
  t: (key: string) => string;
  getLinkUrl: (path: string, targetApp: AppNav) => string;
  featureFlags: Record<string, boolean>;
  walletLink: MenuItem;
  clientInvoicingLink: MenuItem;
}): MenuItem[] {
  const { t, getLinkUrl, walletLink, clientInvoicingLink } = params;

  const profileUrl = getLinkUrl('/profile', APP_EOR);

  return [
    walletLink,
    {
      title: t('sidebar.profile'),
      url: profileUrl,
      icon: <User />,
    },
    clientInvoicingLink,
  ];
}

function buildB2CEmployeeMainItems(params: {
  t: (key: string) => string;
  getLinkUrl: (path: string, targetApp: AppNav) => string;
  featureFlags: Record<string, boolean>;
  walletLink: MenuItem;
  clientInvoicingLink: MenuItem;
}): MenuItem[] {
  const { t, getLinkUrl, walletLink, clientInvoicingLink } = params;

  const employmentUrl = getLinkUrl('/employment', APP_EOR);

  return [
    // TODO - Dashboard to be added here
    // TODO - Monthly inputs to be added here
    walletLink,
    {
      title: t('sidebar.employment'),
      url: employmentUrl,
      icon: <BriefcaseBusiness />,
    },
    clientInvoicingLink,
  ];
}

function buildEmployeeMainItems(params: {
  app: AppNav;
  t: (key: string) => string;
  getLinkUrl: (path: string, targetApp: AppNav) => string;
  featureFlags: Record<string, boolean>;
  walletLink: MenuItem;
  clientInvoicingLink: MenuItem;
  expensesLink: MenuItem;
  isGlobalAdmin: boolean;
  isTeamLead: boolean;
  isEmployed: boolean;
}): MenuItem[] {
  const {
    app,
    t,
    getLinkUrl,
    featureFlags,
    walletLink,
    clientInvoicingLink,
    expensesLink,
    isGlobalAdmin,
    isTeamLead,
    isEmployed,
  } = params;

  const dashboardUrl = getLinkUrl('/', APP_EOR);
  const employmentUrl = getLinkUrl('/employment', APP_EOR);
  const monthlyInputsUrl = getLinkUrl('/monthly-inputs', APP_EOR);
  const payrollUrl = getLinkUrl('/payroll', APP_EOR);
  const billingUrl = getLinkUrl('/billing', APP_EOR);
  const { payrollItems, billingItem } = isEmployed
    ? getEmployedEmployeePayrollAndBilling({
        featureFlags,
        isGlobalAdmin,
        isTeamLead,
        app,
        t,
        getLinkUrl,
        monthlyInputsUrl,
        payrollUrl,
        billingUrl,
      })
    : { payrollItems: [] as MenuItem[], billingItem: null };

  return [
    {
      title: t('sidebar.dashboard'),
      url: dashboardUrl,
      icon: <LayoutGrid />,
    },
    ...(isGlobalAdmin ? [peopleNavItem({ app, t, getLinkUrl })] : []),
    ...absencesNavItems({
      app,
      t,
      getLinkUrl,
      enabled: isEmployed || isGlobalAdmin,
    }),
    expensesLink,
    ...payrollItems,
    ...(isGlobalAdmin
      ? reportsNavItems({
          app,
          t,
          getLinkUrl,
          enabled: true,
        })
      : []),
    walletLink,
    ...(billingItem ? [billingItem] : []),
    {
      title: t('sidebar.employment'),
      url: employmentUrl,
      icon: <BriefcaseBusiness />,
    },
    clientInvoicingLink,
  ];
}

function buildFooterMenuItems(params: {
  isMobile: boolean;
  t: (key: string) => string;
  getLinkUrl: (path: string, targetApp: AppNav) => string;
  knowledgeBaseLink: MenuItem;
  showNotifications: boolean;
  isNotificationsOpen: boolean;
  setIsNotificationsOpen: (open: boolean) => void;
  renderNotificationsMobile: (props: {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
  }) => ReactNode;
  slotNotifications: ReactNode;
  slotLogout: ReactNode;
}): MenuItem[] {
  const {
    isMobile,
    t,
    getLinkUrl,
    knowledgeBaseLink,
    showNotifications,
    isNotificationsOpen,
    setIsNotificationsOpen,
    renderNotificationsMobile,
    slotNotifications,
    slotLogout,
  } = params;

  const settingsUrl = getLinkUrl('/settings', APP_EOR);

  const settingsItem: MenuItem = {
    title: t('sidebar.settings'),
    url: settingsUrl,
    icon: <Settings />,
    category: NOTIFICATION_CATEGORIES.SETTINGS,
    ...(isMobile ? { spaceAbove: true } : { bottom: true }),
  };

  const notificationsItem: MenuItem = {
    title: t('sidebar.notifications'),
    component: isMobile
      ? renderNotificationsMobile({
          isOpen: isNotificationsOpen,
          setIsOpen: setIsNotificationsOpen,
        })
      : slotNotifications,
    ...(isMobile ? {} : { bottom: true }),
  };

  const themeItem: MenuItem = {
    title: t('sidebar.theme'),
    component: <ThemeToggle />,
    ...(isMobile ? {} : { bottom: true }),
  };

  const logoutItem: MenuItem = {
    title: t('sidebar.logout'),
    component: slotLogout,
    bottomSeparated: true,
  };

  const knowledgeBaseFooterItem: MenuItem = {
    ...knowledgeBaseLink,
    ...(isMobile ? {} : { bottom: true }),
  };

  return [
    settingsItem,
    ...(showNotifications ? [notificationsItem] : []),
    knowledgeBaseFooterItem,
    themeItem,
    logoutItem,
  ];
}

export interface AppNavigationProps {
  /** Which app is hosting this navigation (determines base URLs for current vs external app). */
  app: AppNav;
  isEmployer: boolean;
  isContractor: boolean;
  isGigWorker: boolean;
  isB2CStandard: boolean;
  isB2CEmployee: boolean;
  isEmployed: boolean;
  /** Whether the user is a team lead. Team leads see a restricted set of nav items. */
  isTeamLead?: boolean;
  /** Whether the user is a team member. Team members see a fixed set of nav items. */
  isTeamMember?: boolean;
  /** Whether the user is a global admin. Global admins with employee accounts see payroll subitems. */
  isGlobalAdmin?: boolean;
  /** Whether the user can view their own payslip documents. */
  canViewSelfPayslips?: boolean;
  /** Translation function (e.g. from useTranslations()). */
  t: (key: string) => string;
  /** Whether to show the notifications menu item. Default true. */
  showNotifications?: boolean;
  /** Whether to show settings, knowledge base, theme, and logout in the sidebar footer. Default true. */
  showFooterMenuItems?: boolean;
  /** Desktop notifications slot. */
  slotNotifications: ReactNode;
  /** Mobile notifications – receives open state from AppNavigation. */
  renderNotificationsMobile: (props: {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
  }) => ReactNode;
  /** Mobile organisation switcher subview for the profile menu. */
  renderOrganisationSwitcherMobile?: (props: {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
  }) => ReactNode;
  /** Logout button/content. */
  slotLogout: ReactNode;
  /** Mobile profile button initials. */
  mobileProfileInitials?: string;
  /** Mobile profile menu items rendered from the right-side initials button. */
  mobileProfileItems?: MenuItem[];

  /** Base URL for the EOR app. */
  eorBase?: string;
  /** Base URL for the Wallet app. */
  walletBase?: string;
  /** Feature flags. */
  featureFlags: Record<string, boolean>;
  /** Unread notification count per category (e.g. { payroll: 3, team: 1 }). Items/subitems with a `category` show this count as badge; parents with subitems show the sum of their children's counts. */
  notificationCountByCategory?: Partial<Record<NotificationCategory, number>>;
  /** Wallet app sub-navigation items rendered under Wallet in the sidebar. */
  walletSubitems?: MenuItem['items'];
}

export function AppNavigation({
  app,
  isEmployer,
  isContractor,
  isGigWorker,
  isB2CStandard,
  isB2CEmployee,
  isEmployed,
  isTeamLead = false,
  isGlobalAdmin = false,
  canViewSelfPayslips = false,
  t,
  showNotifications = true,
  showFooterMenuItems = true,
  slotNotifications,
  renderNotificationsMobile,
  renderOrganisationSwitcherMobile,
  slotLogout,
  mobileProfileInitials,
  mobileProfileItems = [],
  eorBase,
  walletBase,
  featureFlags,
  notificationCountByCategory,
  walletSubitems,
}: AppNavigationProps) {
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isOrganisationSwitcherOpen, setIsOrganisationSwitcherOpen] = useState(false);
  const isLargeTablet = useIsLargeTablet();

  const getLinkUrl = useCallback(
    (path: string, targetApp: AppNav): string => {
      if (app === targetApp) return path;
      const base = targetApp === APP_WALLET ? walletBase : eorBase;
      return path === '/' ? (base ?? '') : `${base ?? ''}${path}`;
    },
    [app, walletBase, eorBase]
  );

  const walletLink: MenuItem = useMemo(() => {
    const baseUrl = getLinkUrl('/', APP_WALLET);

    if (walletSubitems?.length) {
      const items =
        app === APP_WALLET
          ? walletSubitems
          : walletSubitems.map((sub) => ({
              ...sub,
              url: getLinkUrl(sub.url, APP_WALLET),
              isPathActive: undefined,
            }));

      return {
        title: t('sidebar.wallet'),
        url: baseUrl,
        icon: <WalletCards />,
        items,
      };
    }

    return {
      title: t('sidebar.wallet'),
      url: baseUrl,
      ...(app === APP_WALLET
        ? {
            urlPrefix: '/',
            excludeUrlPrefix: ['/expenses', '/client-invoicing', '/contractors/payments'],
          }
        : {}),
      icon: <WalletCards />,
    };
  }, [app, getLinkUrl, t, walletSubitems]);

  const clientInvoicingLink: MenuItem = useMemo(
    () => ({
      title: t('sidebar.clientInvoicing'),
      url: getLinkUrl('/client-invoicing', APP_WALLET),
      ...(app === APP_WALLET ? { urlPrefix: '/client-invoicing' } : {}),
      icon: <FileText />,
    }),
    [app, getLinkUrl, t]
  );

  const expensesLink: MenuItem = useMemo(
    () => ({
      title: t('sidebar.expenses'),
      url: getLinkUrl('/expenses', APP_WALLET),
      ...(app === APP_WALLET ? { urlPrefix: '/expenses' } : {}),
      icon: <ReceiptText />,
      category: NOTIFICATION_CATEGORIES.EXPENSES,
    }),
    [app, getLinkUrl, t]
  );

  const knowledgeBaseLink: MenuItem = useMemo(
    () => ({
      title: t('sidebar.knowledgeBase'),
      url: KNOWLEDGE_BASE_URL,
      icon: <CircleHelp />,
      openInNewTab: true,
    }),
    [t]
  );

  const contractorPaymentsLink: MenuItem = useMemo(
    () => ({
      title: isEmployer ? t('sidebar.contractorPayments') : t('sidebar.payments'),
      url: getLinkUrl('/contractors/payments', APP_WALLET),
      ...(app === APP_WALLET ? { urlPrefix: '/contractors/payments' } : {}),
      icon: <FileClock />,
    }),
    [app, getLinkUrl, t, isEmployer]
  );

  const buildMenuItems = useCallback(
    (isMobile: boolean): MenuItem[] => {
      const footerItems = showFooterMenuItems
        ? buildFooterMenuItems({
            isMobile,
            t,
            getLinkUrl,
            knowledgeBaseLink,
            showNotifications,
            isNotificationsOpen,
            setIsNotificationsOpen,
            renderNotificationsMobile,
            slotNotifications,
            slotLogout,
          })
        : [];

      let mainItems: MenuItem[];

      if (isEmployer) {
        mainItems = buildEmployerMainItems({
          app,
          t,
          getLinkUrl,
          featureFlags,
          walletLink,
          expensesLink,
          contractorPaymentsLink,
          clientInvoicingLink,
        });
      } else if (isContractor) {
        mainItems = buildContractorMainItems({
          app,
          t,
          getLinkUrl,
          featureFlags,
          walletLink,
          clientInvoicingLink,
          expensesLink,
          contractorPaymentsLink,
          isGlobalAdmin,
          isTeamLead,
        });
      } else if (isGigWorker) {
        mainItems = buildGigWorkerMainItems({
          t,
          getLinkUrl,
          featureFlags,
          walletLink,
          clientInvoicingLink,
        });
      } else if (isB2CEmployee) {
        mainItems = buildB2CEmployeeMainItems({
          t,
          getLinkUrl,
          featureFlags,
          walletLink,
          clientInvoicingLink,
        });
      } else if (isB2CStandard) {
        mainItems = buildB2CStandardMainItems({
          t,
          getLinkUrl,
          featureFlags,
          walletLink,
          clientInvoicingLink,
        });
      } else {
        mainItems = buildEmployeeMainItems({
          app,
          t,
          getLinkUrl,
          featureFlags,
          walletLink,
          clientInvoicingLink,
          expensesLink,
          isGlobalAdmin,
          isTeamLead,
          isEmployed,
        });
      }

      const payslipItems = canViewSelfPayslips ? [payslipsNavItem({ app, t, getLinkUrl })] : [];

      return applyNotificationBadgesToItems(
        [...mainItems, ...payslipItems, ...footerItems],
        notificationCountByCategory
      );
    },
    [
      app,
      canViewSelfPayslips,
      clientInvoicingLink,
      contractorPaymentsLink,
      expensesLink,
      featureFlags,
      getLinkUrl,
      isB2CEmployee,
      isB2CStandard,
      isContractor,
      isEmployed,
      isEmployer,
      isGigWorker,
      isGlobalAdmin,
      isNotificationsOpen,
      isTeamLead,
      knowledgeBaseLink,
      notificationCountByCategory,
      renderNotificationsMobile,
      showNotifications,
      showFooterMenuItems,
      slotLogout,
      slotNotifications,
      t,
      walletLink,
    ]
  );

  const menuItems = buildMenuItems(false);
  const mobileMenuItems = buildMenuItems(true);

  return (
    <>
      {!isLargeTablet && <AppSidebar menuItems={menuItems} />}
      {isLargeTablet && (
        <AppNavbar
          menuItems={mobileMenuItems}
          isNotificationsOpen={isNotificationsOpen}
          setIsNotificationsOpen={setIsNotificationsOpen}
          isOrganisationSwitcherOpen={isOrganisationSwitcherOpen}
          setIsOrganisationSwitcherOpen={setIsOrganisationSwitcherOpen}
          profileInitials={mobileProfileInitials}
          profileItems={[
            ...(renderOrganisationSwitcherMobile
              ? [
                  {
                    title: t('organisationSwitcher.fallbackLabel'),
                    component: renderOrganisationSwitcherMobile({
                      isOpen: isOrganisationSwitcherOpen,
                      setIsOpen: setIsOrganisationSwitcherOpen,
                    }),
                    subviewKey: 'organisationSwitcher' as const,
                  },
                ]
              : []),
            {
              title: t('sidebar.notifications'),
              component: renderNotificationsMobile({
                isOpen: isNotificationsOpen,
                setIsOpen: setIsNotificationsOpen,
              }),
              subviewKey: 'notifications' as const,
            },
            ...mobileProfileItems,
          ]}
        />
      )}
    </>
  );
}
